/**
 * useWerkstattPortal — Supabase Hook für das Werkstatt-Partner-Portal
 * 
 * Verwaltet: Werkstatt-Partner, Fall-Zuordnungen, Nachrichten, Anfragen,
 *            Benachrichtigungen (Realtime), Wissensartikel, Newsletter, Auth
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// Types
export interface WerkstattPartner {
  id: string;
  name: string;
  adresse: string;
  plz: string;
  ort: string;
  telefon: string;
  email: string;
  ansprechpartner: string;
  user_id: string | null;
  aktiv: boolean;
  notizen: string;
  created_at: string;
  updated_at: string;
}

export interface WerkstattFall {
  id: string;
  werkstatt_id: string;
  akte_id: string;
  freigegeben_von: string;
  freigegeben_am: string;
  akten_nummer?: string;
  kennzeichen?: string;
  fahrzeug?: string;
  status?: string;
  datum?: string;
  betrieb?: string;
  auftraggeber_name?: string;
  schadendatum?: string;
}

export interface WerkstattNachricht {
  id: string;
  fall_zuordnung_id: string | null;
  akte_id: string | null;
  werkstatt_id: string;
  absender: 'werkstatt' | 'sv_buero';
  nachricht: string;
  gelesen: boolean;
  created_at: string;
}

export interface WerkstattAnfrage {
  id: string;
  werkstatt_id: string;
  typ: 'kostenvoranschlag' | 'gutachten' | 'sonstiges';
  kennzeichen: string;
  fahrzeug: string;
  schadenbeschreibung: string;
  fotos: string[];
  status: 'neu' | 'angenommen' | 'abgelehnt' | 'erledigt';
  akte_id: string | null;
  bearbeitet_von: string;
  created_at: string;
  updated_at: string;
}

export interface WerkstattBenachrichtigung {
  id: string;
  werkstatt_id: string;
  typ: 'gutachten_fertig' | 'status_aenderung' | 'neue_nachricht' | 'anfrage_beantwortet' | 'dokument_hochgeladen' | 'newsletter';
  titel: string;
  text: string | null;
  akte_id: string | null;
  link: string | null;
  gelesen: boolean;
  created_at: string;
}

export interface WerkstattWissen {
  id: string;
  titel: string;
  kategorie: 'versicherungsrecht' | 'schadenabwicklung' | 'tipps' | 'neuigkeiten' | 'technik' | 'recht';
  inhalt: string;
  teaser: string | null;
  bild_url: string | null;
  veroeffentlicht: boolean;
  autor: string;
  created_at: string;
  updated_at: string;
}

export interface WerkstattNewsletter {
  id: string;
  betreff: string;
  teaser: string | null;
  wissen_id: string | null;
  empfaenger_count: number;
  versendet_am: string | null;
  created_at: string;
}

// ============ Auth Hook (Werkstatt-Login) ============
export function useWerkstattAuth() {
  const [user, setUser] = useState<any>(null);
  const [werkstattId, setWerkstattId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Werkstatt-Partner anhand user_id laden
        const { data } = await supabase
          .from('werkstatt_partner')
          .select('id, aktiv')
          .eq('user_id', session.user.id)
          .single();
        if (data && data.aktiv) {
          setWerkstattId(data.id);
        } else if (data && !data.aktiv) {
          setError('Ihr Zugang wurde deaktiviert. Bitte kontaktieren Sie das SV Büro Koop.');
        }
      }
      setLoading(false);
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setUser(session.user);
        const { data } = await supabase
          .from('werkstatt_partner')
          .select('id, aktiv')
          .eq('user_id', session.user.id)
          .single();
        if (data && data.aktiv) {
          setWerkstattId(data.id);
          setError(null);
        } else if (data && !data.aktiv) {
          setError('Ihr Zugang wurde deaktiviert.');
        } else {
          setError('Kein Werkstatt-Profil gefunden. Bitte kontaktieren Sie das SV Büro Koop.');
        }
      } else {
        setUser(null);
        setWerkstattId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      if (authError.message.includes('Invalid login')) {
        setError('E-Mail oder Passwort ist falsch.');
      } else {
        setError(authError.message);
      }
      return { error: authError };
    }
    return { data };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setWerkstattId(null);
  };

  const resetPassword = async (email: string) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/werkstatt/login?reset=true`,
    });
    return { error: resetError };
  };

  return { user, werkstattId, loading, error, login, logout, resetPassword };
}

// ============ Benachrichtigungen Hook (Realtime) ============
export function useWerkstattBenachrichtigungen(werkstattId?: string) {
  const [benachrichtigungen, setBenachrichtigungen] = useState<WerkstattBenachrichtigung[]>([]);
  const [ungelesen, setUngelesen] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<any>(null);

  const fetchBenachrichtigungen = useCallback(async () => {
    if (!werkstattId) return;
    const { data } = await supabase
      .from('werkstatt_benachrichtigungen')
      .select('*')
      .eq('werkstatt_id', werkstattId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) {
      setBenachrichtigungen(data);
      setUngelesen(data.filter(b => !b.gelesen).length);
    }
    setLoading(false);
  }, [werkstattId]);

  useEffect(() => {
    fetchBenachrichtigungen();

    // Realtime subscription
    if (werkstattId) {
      channelRef.current = supabase
        .channel(`werkstatt_benachrichtigungen_${werkstattId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'werkstatt_benachrichtigungen',
          filter: `werkstatt_id=eq.${werkstattId}`,
        }, (payload: any) => {
          const newItem = payload.new as WerkstattBenachrichtigung;
          setBenachrichtigungen(prev => [newItem, ...prev]);
          setUngelesen(prev => prev + 1);
        })
        .subscribe();
    }

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [werkstattId, fetchBenachrichtigungen]);

  const markAsRead = async (id: string) => {
    const { error } = await supabase
      .from('werkstatt_benachrichtigungen')
      .update({ gelesen: true })
      .eq('id', id);
    if (!error) {
      setBenachrichtigungen(prev => prev.map(b => b.id === id ? { ...b, gelesen: true } : b));
      setUngelesen(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    if (!werkstattId) return;
    const { error } = await supabase
      .from('werkstatt_benachrichtigungen')
      .update({ gelesen: true })
      .eq('werkstatt_id', werkstattId)
      .eq('gelesen', false);
    if (!error) {
      setBenachrichtigungen(prev => prev.map(b => ({ ...b, gelesen: true })));
      setUngelesen(0);
    }
  };

  return { benachrichtigungen, ungelesen, loading, markAsRead, markAllAsRead, refresh: fetchBenachrichtigungen };
}

// ============ Wissen Hook (Artikel lesen) ============
export function useWerkstattWissen() {
  const [artikel, setArtikel] = useState<WerkstattWissen[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchArtikel = useCallback(async () => {
    const { data } = await supabase
      .from('werkstatt_wissen')
      .select('*')
      .eq('veroeffentlicht', true)
      .order('created_at', { ascending: false });
    if (data) setArtikel(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchArtikel(); }, [fetchArtikel]);

  return { artikel, loading, refresh: fetchArtikel };
}

// ============ Wissen Admin Hook (SV-Büro: Artikel erstellen/bearbeiten) ============
export function useWerkstattWissenAdmin() {
  const [artikel, setArtikel] = useState<WerkstattWissen[]>([]);
  const [newsletter, setNewsletter] = useState<WerkstattNewsletter[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchArtikel = useCallback(async () => {
    const { data } = await supabase
      .from('werkstatt_wissen')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setArtikel(data);
  }, []);

  const fetchNewsletter = useCallback(async () => {
    const { data } = await supabase
      .from('werkstatt_newsletter')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setNewsletter(data);
  }, []);

  useEffect(() => {
    Promise.all([fetchArtikel(), fetchNewsletter()]).finally(() => setLoading(false));
  }, [fetchArtikel, fetchNewsletter]);

  const createArtikel = async (a: Partial<WerkstattWissen>) => {
    const { data, error } = await supabase.from('werkstatt_wissen').insert(a).select().single();
    if (data) setArtikel(prev => [data, ...prev]);
    return { data, error };
  };

  const updateArtikel = async (id: string, updates: Partial<WerkstattWissen>) => {
    const { data, error } = await supabase
      .from('werkstatt_wissen')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (data) setArtikel(prev => prev.map(a => a.id === id ? data : a));
    return { data, error };
  };

  const deleteArtikel = async (id: string) => {
    const { error } = await supabase.from('werkstatt_wissen').delete().eq('id', id);
    if (!error) setArtikel(prev => prev.filter(a => a.id !== id));
    return { error };
  };

  const sendNewsletter = async (wissenId: string, betreff: string, teaser: string) => {
    // Get active partner count
    const { count } = await supabase
      .from('werkstatt_partner')
      .select('*', { count: 'exact', head: true })
      .eq('aktiv', true);

    // Create newsletter record
    const { data, error } = await supabase
      .from('werkstatt_newsletter')
      .insert({
        betreff,
        teaser,
        wissen_id: wissenId,
        empfaenger_count: count || 0,
        versendet_am: new Date().toISOString(),
      })
      .select()
      .single();

    if (data) setNewsletter(prev => [data, ...prev]);

    // Create benachrichtigungen for all active partners
    const { data: partners } = await supabase
      .from('werkstatt_partner')
      .select('id')
      .eq('aktiv', true);

    if (partners && partners.length > 0) {
      const benachrichtigungen = partners.map(p => ({
        werkstatt_id: p.id,
        typ: 'newsletter',
        titel: betreff,
        text: teaser,
        link: `/werkstatt/wissen`,
      }));
      await supabase.from('werkstatt_benachrichtigungen').insert(benachrichtigungen);
    }

    return { data, error, empfaenger: count || 0 };
  };

  return {
    artikel, newsletter, loading,
    createArtikel, updateArtikel, deleteArtikel,
    sendNewsletter,
    refresh: () => Promise.all([fetchArtikel(), fetchNewsletter()]),
  };
}

// ============ Admin Hook (SV-Büro Seite) ============
export function useWerkstattAdmin() {
  const [partner, setPartner] = useState<WerkstattPartner[]>([]);
  const [anfragen, setAnfragen] = useState<WerkstattAnfrage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPartner = useCallback(async () => {
    const { data } = await supabase
      .from('werkstatt_partner')
      .select('*')
      .order('name');
    if (data) setPartner(data);
  }, []);

  const fetchAnfragen = useCallback(async () => {
    const { data } = await supabase
      .from('werkstatt_anfragen')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setAnfragen(data);
  }, []);

  useEffect(() => {
    Promise.all([fetchPartner(), fetchAnfragen()]).finally(() => setLoading(false));
  }, [fetchPartner, fetchAnfragen]);

  const createPartner = async (p: Partial<WerkstattPartner>) => {
    const { data, error } = await supabase.from('werkstatt_partner').insert(p).select().single();
    if (data) setPartner(prev => [...prev, data]);
    return { data, error };
  };

  const updatePartner = async (id: string, updates: Partial<WerkstattPartner>) => {
    const { data, error } = await supabase.from('werkstatt_partner').update(updates).eq('id', id).select().single();
    if (data) setPartner(prev => prev.map(p => p.id === id ? data : p));
    return { data, error };
  };

  const deletePartner = async (id: string) => {
    const { error } = await supabase.from('werkstatt_partner').delete().eq('id', id);
    if (!error) setPartner(prev => prev.filter(p => p.id !== id));
    return { error };
  };

  const assignAkte = async (werkstattId: string, akteId: string, freigegebenVon: string) => {
    const { data, error } = await supabase
      .from('werkstatt_fall_zuordnung')
      .insert({ werkstatt_id: werkstattId, akte_id: akteId, freigegeben_von: freigegebenVon })
      .select()
      .single();
    return { data, error };
  };

  const removeAkteAccess = async (werkstattId: string, akteId: string) => {
    const { error } = await supabase
      .from('werkstatt_fall_zuordnung')
      .delete()
      .eq('werkstatt_id', werkstattId)
      .eq('akte_id', akteId);
    return { error };
  };

  const getWerkstattFuerAkte = async (akteId: string) => {
    const { data } = await supabase
      .from('werkstatt_fall_zuordnung')
      .select('*, werkstatt_partner(*)')
      .eq('akte_id', akteId);
    return data || [];
  };

  const updateAnfrageStatus = async (id: string, status: string, bearbeitetVon: string) => {
    const { data, error } = await supabase
      .from('werkstatt_anfragen')
      .update({ status, bearbeitet_von: bearbeitetVon, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (data) setAnfragen(prev => prev.map(a => a.id === id ? data : a));
    return { data, error };
  };

  // Werkstatt-User in Supabase Auth anlegen und mit Partner verknüpfen
  const createWerkstattUser = async (partnerId: string, email: string, password: string) => {
    // Supabase Auth User anlegen (admin API nicht verfügbar im Frontend)
    // Stattdessen: signUp + Partner-Verknüpfung
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { role: 'werkstatt', werkstatt_partner_id: partnerId },
      },
    });

    if (authError) return { error: authError };

    // Partner mit user_id verknüpfen
    if (authData.user) {
      await supabase
        .from('werkstatt_partner')
        .update({ user_id: authData.user.id })
        .eq('id', partnerId);
    }

    return { data: authData };
  };

  // Benachrichtigung an Werkstatt senden
  const sendBenachrichtigung = async (
    werkstattId: string,
    typ: WerkstattBenachrichtigung['typ'],
    titel: string,
    text?: string,
    akteId?: string,
    link?: string
  ) => {
    const { data, error } = await supabase
      .from('werkstatt_benachrichtigungen')
      .insert({ werkstatt_id: werkstattId, typ, titel, text, akte_id: akteId, link })
      .select()
      .single();
    return { data, error };
  };

  return {
    partner, anfragen, loading,
    createPartner, updatePartner, deletePartner,
    assignAkte, removeAkteAccess, getWerkstattFuerAkte,
    updateAnfrageStatus, createWerkstattUser, sendBenachrichtigung,
    refresh: () => Promise.all([fetchPartner(), fetchAnfragen()]),
  };
}

// ============ Portal Hook (Werkstatt-Seite) ============
export function useWerkstattPortal(werkstattId?: string) {
  const [werkstatt, setWerkstatt] = useState<WerkstattPartner | null>(null);
  const [faelle, setFaelle] = useState<WerkstattFall[]>([]);
  const [nachrichten, setNachrichten] = useState<WerkstattNachricht[]>([]);
  const [anfragen, setAnfragen] = useState<WerkstattAnfrage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWerkstatt = useCallback(async (id: string) => {
    const { data } = await supabase.from('werkstatt_partner').select('*').eq('id', id).single();
    if (data) setWerkstatt(data);
    return data;
  }, []);

  const fetchFaelle = useCallback(async (id: string) => {
    const { data } = await supabase
      .from('werkstatt_fall_zuordnung')
      .select('*')
      .eq('werkstatt_id', id)
      .order('freigegeben_am', { ascending: false });
    
    if (data && data.length > 0) {
      const akteIds = data.map(f => f.akte_id);
      const { data: akten } = await supabase
        .from('akten')
        .select('id, akten_nummer, kennzeichen, fahrzeug, status, created_at, betrieb, auftraggeber_name, schadendatum')
        .in('id', akteIds);
      
      const aktenMap = new Map((akten || []).map(a => [a.id, a]));
      const enriched = data.map(f => ({
        ...f,
        ...(aktenMap.get(f.akte_id) || {}),
        datum: aktenMap.get(f.akte_id)?.created_at,
      }));
      setFaelle(enriched);
    } else {
      setFaelle([]);
    }
  }, []);

  const fetchNachrichten = useCallback(async (id: string) => {
    const { data } = await supabase
      .from('werkstatt_nachrichten')
      .select('*')
      .eq('werkstatt_id', id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (data) setNachrichten(data);
  }, []);

  const fetchAnfragen = useCallback(async (id: string) => {
    const { data } = await supabase
      .from('werkstatt_anfragen')
      .select('*')
      .eq('werkstatt_id', id)
      .order('created_at', { ascending: false });
    if (data) setAnfragen(data);
  }, []);

  useEffect(() => {
    if (!werkstattId) { setLoading(false); return; }
    Promise.all([
      fetchWerkstatt(werkstattId),
      fetchFaelle(werkstattId),
      fetchNachrichten(werkstattId),
      fetchAnfragen(werkstattId),
    ]).finally(() => setLoading(false));
  }, [werkstattId, fetchWerkstatt, fetchFaelle, fetchNachrichten, fetchAnfragen]);

  const sendNachricht = async (akteId: string | null, nachricht: string) => {
    if (!werkstattId) return { error: 'Keine Werkstatt-ID' };
    const { data, error } = await supabase
      .from('werkstatt_nachrichten')
      .insert({
        werkstatt_id: werkstattId,
        akte_id: akteId,
        absender: 'werkstatt',
        nachricht,
      })
      .select()
      .single();
    if (data) setNachrichten(prev => [data, ...prev]);
    return { data, error };
  };

  const createAnfrage = async (anfrage: Partial<WerkstattAnfrage>) => {
    if (!werkstattId) return { error: 'Keine Werkstatt-ID' };
    const { data, error } = await supabase
      .from('werkstatt_anfragen')
      .insert({ ...anfrage, werkstatt_id: werkstattId })
      .select()
      .single();
    if (data) setAnfragen(prev => [data, ...prev]);
    return { data, error };
  };

  const updateProfil = async (updates: Partial<WerkstattPartner>) => {
    if (!werkstattId) return { error: 'Keine Werkstatt-ID' };
    const { data, error } = await supabase
      .from('werkstatt_partner')
      .update(updates)
      .eq('id', werkstattId)
      .select()
      .single();
    if (data) setWerkstatt(data);
    return { data, error };
  };

  return {
    werkstatt, faelle, nachrichten, anfragen, loading,
    sendNachricht, createAnfrage, updateProfil,
    refresh: () => werkstattId ? Promise.all([
      fetchFaelle(werkstattId),
      fetchNachrichten(werkstattId),
      fetchAnfragen(werkstattId),
    ]) : Promise.resolve(),
  };
}
